import React, { useState, useMemo } from "react";
import {
  useCreateSocialPostMutation,
  useGetSocialSubscribersQuery,
} from "../../apis/socialPost";
import { useGetOrganizationsQuery } from "../../apis/organization";
import moment from "moment-timezone";
import Modal from "components/ui/Modal";
import Button from "components/ui/Button";

const COMPANY_COVER_REASONS = {
  past_due: "the office subscription payment failed",
  unpaid: "the office subscription is unpaid",
  canceled: "the office subscription was cancelled",
  expired: "the office subscription expired",
  paused: "the office subscription is paused",
};

// The agent's own period end goes stale once the office takes over billing.
const describeLapsedSubscription = (agent) => {
  const cover = agent.companyCoverage;
  if (cover) {
    const reason =
      COMPANY_COVER_REASONS[cover.status] ||
      `the office subscription is ${cover.status}`;
    return `Cannot post — ${reason}. Fix billing for the office, not this agent.`;
  }
  return agent.currentPeriodEnd &&
    moment(agent.currentPeriodEnd).isBefore(moment())
    ? `Social subscription expired ${moment(agent.currentPeriodEnd).format("D MMM YYYY")} — renew before posting.`
    : `Social subscription ${agent.subscriptionStatus || "inactive"} — renew before posting.`;
};

const AddSocialPost = ({ isOpen, onClose }) => {
  const [caption, setCaption] = useState("");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [type, setType] = useState("post");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [agentSearch, setAgentSearch] = useState("");
  const [includeCollaborators, setIncludeCollaborators] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isInstant, setIsInstant] = useState(false);
  const [errors, setErrors] = useState({});

  const { data: allUsers = [] } = useGetSocialSubscribersQuery();
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { mutate: createBulkPost, isPending } = useCreateSocialPostMutation();

  // The org and the subscription state travel with the subscriber entry, not the nested user.
  const eligibleAgents = useMemo(() => {
    return allUsers
      .filter((item) => item.user)
      .map((item) => ({
        ...item.user,
        organizationId: item.organization?._id?.toString() || null,
        isActive: item.isActive,
        subscriptionStatus: item.status,
        currentPeriodEnd: item.currentPeriodEnd,
        companyCoverage: item.companyCoverage || null,
      }));
  }, [allUsers]);

  // One post never spans organizations, so the org narrows the list before
  // anything else — including "Select All".
  const filteredAgents = useMemo(() => {
    if (!selectedOrganizationId) return [];

    return eligibleAgents.filter((agent) => {
      if (agent.organizationId !== selectedOrganizationId) return false;
      if (selectedPlatforms.length === 0) return true;
      if (!agent.platforms || agent.platforms.length === 0) return false;
      return selectedPlatforms.some((platform) =>
        agent.platforms.includes(platform),
      );
    });
  }, [eligibleAgents, selectedPlatforms, selectedOrganizationId]);

  // Search narrows what is shown, never what is selected — an agent ticked
  // before typing stays in the post.
  const visibleAgents = useMemo(() => {
    const term = agentSearch.trim().toLowerCase();
    if (!term) return filteredAgents;
    return filteredAgents.filter((agent) =>
      [agent.name, agent.email, agent.instagramUsername]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term)),
    );
  }, [filteredAgents, agentSearch]);

  // A lapsed subscription is rejected by the backend, so it can never be ticked.
  const selectableAgents = useMemo(
    () => filteredAgents.filter((agent) => agent.isActive),
    [filteredAgents],
  );

  const visibleSelectableAgents = useMemo(
    () => visibleAgents.filter((agent) => agent.isActive),
    [visibleAgents],
  );

  const allVisibleSelected =
    visibleSelectableAgents.length > 0 &&
    visibleSelectableAgents.every((agent) => selectedAgents.includes(agent._id));

  // Agents already ticked belong to the previous org, so they cannot survive the
  // switch — that is how a stray id would end up in the submission.
  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganizationId(organizationId);
    setSelectedAgents([]);
    setAgentSearch("");
  };

  const handlePlatformChange = (platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform],
    );
    setSelectedAgents([]);
  };

  const handleAgentChange = (agentId) => {
    setSelectedAgents((prev) =>
      prev.includes(agentId)
        ? prev.filter((id) => id !== agentId)
        : [...prev, agentId],
    );
  };

  const handleSelectAllAgents = () => {
    const visibleIds = visibleSelectableAgents.map((agent) => agent._id);
    setSelectedAgents((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds])),
    );
  };

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 10) {
      setErrors((prev) => ({ ...prev, media: "Maximum 10 files allowed" }));
      return;
    }
    const invalidFiles = files.filter((file) => file.size > 100 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      setErrors((prev) => ({
        ...prev,
        media: "Each file must be less than 100MB",
      }));
      return;
    }

    setMediaFiles(files);
    setErrors((prev) => ({ ...prev, media: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedOrganizationId) {
      newErrors.organization = "Select an organization";
    }

    if (!caption.trim()) {
      newErrors.caption = "Caption is required";
    }

    if (mediaFiles.length === 0) {
      newErrors.media = "At least one media file is required";
    }

    if (selectedPlatforms.length === 0) {
      newErrors.platforms = "Select at least one platform";
    }

    if (selectedAgents.length === 0) {
      newErrors.agents = "Select at least one agent";
    }

    if (!isInstant && (!scheduleDate || !scheduleTime)) {
      newErrors.schedule = "Schedule date and time are required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData = new FormData();

    mediaFiles.forEach((file) => {
      formData.append("media", file);
    });

    formData.append("caption", caption);

    formData.append("organizationId", selectedOrganizationId);

    // Add type
    formData.append("type", type);

    // Add platforms
    formData.append("platforms", JSON.stringify(selectedPlatforms));

    // Add agent IDs
    formData.append("agentIds", JSON.stringify([...selectedAgents]));

    // Add include collaborators
    if (includeCollaborators) {
      formData.append("includeCollaborators", "true");
    }

    // Add schedule date
    let scheduleDateTimeISO;
    if (isInstant) {
      scheduleDateTimeISO = moment().toISOString();
    } else {
      scheduleDateTimeISO = moment(
        `${scheduleDate} ${scheduleTime}`,
        "YYYY-MM-DD HH:mm",
      ).toISOString();
    }
    formData.append("scheduleDate", scheduleDateTimeISO);

    createBulkPost(formData, {
      onSuccess: (response) => {
        // Nothing went out — keep the form so the media pick survives a retry.
        if (!response?.data?.data?.postsCreated) return;
        handleClose();
      },
    });
  };

  const handleClose = () => {
    setCaption("");
    setSelectedOrganizationId("");
    setType("post");
    setSelectedPlatforms([]);
    setMediaFiles([]);
    setSelectedAgents([]);
    setAgentSearch("");
    setIncludeCollaborators(false);
    setScheduleDate("");
    setScheduleTime("");
    setIsInstant(false);
    setErrors({});
    onClose();
  };

  return (
    <Modal
      className="max-w-xl"
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Social Post"
    >
      <form onSubmit={handleSubmit}>
        {/* Organization */}
        <div className="mb-4">
          <label className="form-label">
            Organization <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedOrganizationId}
            onChange={(e) => handleOrganizationChange(e.target.value)}
            className={`form-input ${
              errors.organization ? "border-red-500" : "border-mediumgray"
            } border`}
          >
            <option value="">Select an organization</option>
            {organizations.map((organization) => (
              <option key={organization._id} value={organization._id}>
                {organization.name}
              </option>
            ))}
          </select>
          {errors.organization && (
            <p className="form-error-message mt-1">{errors.organization}</p>
          )}
          <p className="text-xs text-darkgray mt-1">
            A post goes to one organization only.
          </p>
        </div>

        {/* Caption */}
        <div className="mb-4">
          <label className="form-label">
            Caption <span className="text-red-500">*</span>
          </label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Enter caption... Use {{AGENT_NAME}} for dynamic agent name"
            className={`form-textarea ${
              errors.caption ? "border-red-500" : "border-mediumgray"
            } border`}
            rows="4"
          />
          {errors.caption && (
            <p className="form-error-message mt-1">{errors.caption}</p>
          )}
          <p className="text-xs text-darkgray mt-1">
            Tip: Use {"{{AGENT_NAME}}"} to insert agent's name dynamically
          </p>
        </div>

        {/* Post Type */}
        <div className="mb-4">
          <label className="form-label">
            Post Type <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                value="post"
                checked={type === "post"}
                onChange={(e) => setType(e.target.value)}
                className="mr-2"
              />
              <span>Post</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                value="reel"
                checked={type === "reel"}
                onChange={(e) => setType(e.target.value)}
                className="mr-2"
              />
              <span>Reel</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                value="story"
                checked={type === "story"}
                onChange={(e) => setType(e.target.value)}
                className="mr-2"
              />
              <span>Story</span>
            </label>
          </div>
        </div>

        {/* Media Upload */}
        <div className="mb-4">
          <label className="form-label">
            Media Files <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleMediaChange}
            className={`form-input ${
              errors.media ? "border-red-500" : "border-mediumgray"
            } border`}
          />
          {errors.media && (
            <p className="form-error-message mt-1">{errors.media}</p>
          )}
          <p className="text-xs text-darkgray mt-1">
            Maximum 10 files, each up to 100MB
          </p>
          {mediaFiles.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-darkergray">
                {mediaFiles.length} file(s) selected
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {Array.from(mediaFiles).map((file, index) => (
                  <div
                    key={index}
                    className="text-xs bg-lightgray px-2 py-1 rounded"
                  >
                    {file.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Platforms */}
        <div className="mb-4">
          <label className="form-label">
            Platforms <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectedPlatforms.includes("instagram")}
                onChange={() => handlePlatformChange("instagram")}
                className="mr-2"
              />
              <span>Instagram</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectedPlatforms.includes("facebook")}
                onChange={() => handlePlatformChange("facebook")}
                className="mr-2"
              />
              <span>Facebook</span>
            </label>
          </div>
          {errors.platforms && (
            <p className="form-error-message mt-1">{errors.platforms}</p>
          )}
        </div>

        {/* Agents Selection */}
        <div className="mb-4">
          <label className="form-label">
            Select Agents <span className="text-red-500">*</span>
          </label>
          {!selectedOrganizationId ? (
            <p className="text-sm text-darkgray italic">
              Select an organization first
            </p>
          ) : selectedPlatforms.length === 0 ? (
            <p className="text-sm text-darkgray italic">
              Please select platforms first
            </p>
          ) : filteredAgents.length === 0 ? (
            <p className="text-sm text-red-500">
              No subscribed agents in this organization for the selected
              platforms
            </p>
          ) : (
            <>
              <input
                type="text"
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
                placeholder="Search agents by name or email..."
                className="form-input border border-mediumgray mb-2"
              />
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={handleSelectAllAgents}
                    className="mr-2"
                  />
                  <span className="font-semibold">
                    {agentSearch.trim()
                      ? `Select these ${visibleSelectableAgents.length}`
                      : `Select All (${selectableAgents.length})`}
                  </span>
                </label>
                {agentSearch.trim() && (
                  <button
                    type="button"
                    onClick={() => setAgentSearch("")}
                    className="text-xs text-darkgray hover:text-black"
                  >
                    Clear search
                  </button>
                )}
              </div>
              <div className="border border-mediumgray rounded max-h-60 overflow-y-auto p-3">
                {visibleAgents.length === 0 && (
                  <p className="text-sm text-darkgray italic">
                    No agents match "{agentSearch.trim()}".
                  </p>
                )}
                {visibleAgents.map((agent) => (
                  <label
                    key={agent._id}
                    className={`flex items-center mb-2 p-2 rounded ${
                      agent.isActive
                        ? "cursor-pointer hover:bg-lightergray"
                        : "cursor-not-allowed bg-red-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAgents.includes(agent._id)}
                      onChange={() => handleAgentChange(agent._id)}
                      disabled={!agent.isActive}
                      className="mr-2"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{agent.name}</p>
                      <p className="text-xs text-darkgray">{agent.email}</p>
                      <p className="text-xs text-mediumgray">
                        Platforms: {agent.platforms?.join(", ") || "N/A"}
                      </p>
                      {!agent.isActive && (
                        <p className="text-xs text-red-500">
                          {describeLapsedSubscription(agent)}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
          {errors.agents && (
            <p className="form-error-message mt-1">{errors.agents}</p>
          )}
          {selectedAgents.length > 0 && (
            <p className="text-sm text-darkergray mt-1">
              {selectedAgents.length} agent(s) selected
            </p>
          )}
        </div>

        {/* Include Collaborators */}
        <div className="mb-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={includeCollaborators}
              onChange={(e) => setIncludeCollaborators(e.target.checked)}
              className="mr-2"
            />
            <span>Include agent collaborators in posting</span>
          </label>
        </div>

        {/* Schedule Options */}
        <div className="mb-4">
          <label className="form-label">Schedule</label>
          <div className="mb-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isInstant}
                onChange={(e) => setIsInstant(e.target.checked)}
                className="mr-2"
              />
              <span>Post Instantly</span>
            </label>
          </div>

          {!isInstant && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label text-sm">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={moment().format("YYYY-MM-DD")}
                  className={`form-input ${
                    errors.schedule ? "border-red-500" : "border-mediumgray"
                  } border`}
                />
              </div>
              <div>
                <label className="form-label text-sm">
                  Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className={`form-input ${
                    errors.schedule ? "border-red-500" : "border-mediumgray"
                  } border`}
                />
              </div>
            </div>
          )}
          {errors.schedule && (
            <p className="form-error-message mt-1">{errors.schedule}</p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-lightgray">
          <Button
            type="button"
            onClick={handleClose}
            className="gray-button"
            disabled={isPending}
            loading={isPending}
          >
            Cancel
          </Button>
          <Button
            loading={isPending}
            type="submit"
            className="black-button"
            disabled={isPending}
          >
            {isInstant ? "Post Now" : "Schedule Post"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSocialPost;
